import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { PlannerAction, PlannerState } from '../types';
import { formatDelta, formatDistance, formatDuration } from '../geo/format';
import { routeStreetSummary } from '../io/missionRoute';
import { MAX_NAMED_ROUTES, NamedRouteSlot } from '../../missions/namedRouteEditor';
import { metalSx } from '../../../theme/metalButtons';

const GOLD = '#f5b942';
const TEAL = '#00b8a9';

const Action: React.FC<{
  onClick?: () => void;
  disabled?: boolean;
  tone?: string;
  children: React.ReactNode;
}> = ({ onClick, disabled, tone, children }) => (
  <Typography
    component="span"
    role="button"
    tabIndex={disabled ? -1 : 0}
    variant="caption"
    onClick={(e) => {
      e.stopPropagation();
      if (!disabled) onClick?.();
    }}
    onKeyDown={(e) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); }
    }}
    sx={{
      fontWeight: 800,
      cursor: disabled ? 'default' : 'pointer',
      color: disabled ? 'text.disabled' : (tone || 'text.primary'),
      '&:hover': disabled ? undefined : { textDecoration: 'underline' },
      '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
    }}
  >
    {children}
  </Typography>
);

export interface NamedRoutesProps {
  state: PlannerState;
  dispatch: React.Dispatch<PlannerAction>;
  slots?: NamedRouteSlot[];
  editingId?: string | null;
  onSelectSlot?: (id: string) => void;
  onAddSlot?: () => void;
  onUseSlot?: (id: string) => void;
  onDeleteSlot?: (id: string) => void;
  canAdd?: boolean;
  saveLabel?: string;
  saving?: boolean;
  onSave?: () => void;
}

const NamedRoutes: React.FC<NamedRoutesProps> = ({
  state,
  dispatch,
  slots = [],
  editingId,
  onSelectSlot,
  onAddSlot,
  onUseSlot,
  onDeleteSlot,
  canAdd = true,
  saveLabel = 'Save route',
  saving,
  onSave,
}) => {
  const primary = state.routes[state.primaryIndex] || null;
  const others = state.routes
    .map((route, index) => ({ route, index }))
    .filter((item) => item.index !== state.primaryIndex);
  const fallback = state.alternateIndex == null
    ? null
    : state.routes[state.alternateIndex] || null;
  const editing = slots.find((slot) => slot.id === editingId) || null;

  if (!primary && slots.length === 0) return null;

  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
        Named routes
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        Primary is the live line. Add Secondary or Third with different
        stops or ends, then Use one to switch before or during the run.
        Fallback is a Mapbox option for the same stops as the route you
        are editing.
      </Typography>

      {slots.map((slot) => (
        <Box
          key={slot.id}
          onClick={() => onSelectSlot?.(slot.id)}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.75,
            py: 0.3,
            px: 0.5,
            mb: 0.25,
            borderRadius: 0.75,
            cursor: onSelectSlot ? 'pointer' : 'default',
            borderLeft: '2px solid',
            borderColor: slot.id === editingId ? GOLD : slot.live ? '#4c8bf5' : 'transparent',
            '&:hover': onSelectSlot ? { bgcolor: 'action.hover' } : undefined,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, minWidth: 0 }} noWrap>
            {slot.label}
          </Typography>
          {slot.live && (
            <Typography variant="caption" sx={{ color: '#4c8bf5', fontWeight: 800, fontSize: '0.6rem' }}>
              LIVE
            </Typography>
          )}
          {slot.draft && (
            <Typography variant="caption" color="text.secondary">draft</Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {slot.duration != null && (
            <Typography variant="caption" color="text.secondary">
              {formatDuration(slot.duration)}
            </Typography>
          )}
          {!slot.live && !slot.draft && slot.geometry && onUseSlot && (
            <Action
              tone={TEAL}
              onClick={() => { onUseSlot(slot.id); }}
            >
              Use
            </Action>
          )}
          {!slot.live && onDeleteSlot && (
            <Action onClick={() => { onDeleteSlot(slot.id); }}>
              Remove
            </Action>
          )}
        </Box>
      ))}

      {(onAddSlot || onSave) && (
        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, mb: 1.1 }}>
          {onAddSlot && (
            <Box
              component="button"
              type="button"
              disabled={!canAdd}
              onClick={() => { if (canAdd) onAddSlot(); }}
              title={canAdd ? 'Add another named route' : `${MAX_NAMED_ROUTES} named routes max`}
              sx={{
                ...metalSx('steel'),
                display: 'flex',
                flex: 1,
                height: 32,
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'inherit',
                fontSize: '0.72rem',
                letterSpacing: 0.4,
                cursor: canAdd ? 'pointer' : 'default',
              }}
            >
              {canAdd ? 'Add route' : `${MAX_NAMED_ROUTES} routes max`}
            </Box>
          )}
          {onSave && (
            <Box
              component="button"
              type="button"
              disabled={!primary || !!saving}
              onClick={() => { if (primary && !saving) onSave(); }}
              title={saving ? 'Saving…' : saveLabel}
              sx={{
                ...metalSx('steel'),
                display: 'flex',
                flex: 1,
                height: 32,
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'inherit',
                fontSize: '0.72rem',
                letterSpacing: 0.4,
                cursor: primary && !saving ? 'pointer' : 'default',
              }}
            >
              {saving ? 'Saving…' : saveLabel}
            </Box>
          )}
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        label="Route name"
        placeholder={editing?.label || 'e.g. Secondary'}
        value={state.primaryName}
        onChange={(e) => dispatch({ type: 'SET_ROUTE_NAME', role: 'primary', name: e.target.value })}
        sx={{ mb: 0.5 }}
      />
      {primary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {formatDuration(primary.duration)}
          {' · '}
          {formatDistance(primary.distance, state.options.units)}
          {routeStreetSummary(primary) ? ` · ${routeStreetSummary(primary)}` : ''}
          {editing && !editing.live ? ' · not live yet' : ''}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: fallback ? TEAL : 'text.primary' }}>
          Fallback
        </Typography>
        {fallback && (
          <Action tone={TEAL} onClick={() => dispatch({ type: 'SET_ALTERNATE', index: null })}>
            Remove
          </Action>
        )}
      </Box>

      {fallback ? (
        <>
          <TextField
            fullWidth
            size="small"
            label="Fallback name"
            placeholder={state.primaryName.trim() ? `${state.primaryName.trim()} Alternate` : 'e.g. Test 3 Alternate'}
            value={state.alternateName}
            onChange={(e) => dispatch({ type: 'SET_ROUTE_NAME', role: 'alternate', name: e.target.value })}
            sx={{ mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {formatDuration(fallback.duration)}
            {' · '}
            {formatDistance(fallback.distance, state.options.units)}
            {routeStreetSummary(fallback) ? ` · ${routeStreetSummary(fallback)}` : ''}
          </Typography>
        </>
      ) : !primary ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Drop a start and end for this route. They can differ from the live line.
        </Typography>
      ) : others.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Mapbox only returned one line for these stops. Move a stop or change
          the avoidances if you need another option to keep.
        </Typography>
      ) : (
        others.map(({ route, index }) => (
          <Box
            key={route.id}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1,
              py: 0.35,
              px: 0.5,
              borderRadius: 0.75,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 54 }}>
              {formatDuration(route.duration)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {formatDelta(
                route.duration - primary.duration,
                route.distance - primary.distance,
                state.options.units,
              )}
              {routeStreetSummary(route) ? ` · ${routeStreetSummary(route)}` : ''}
            </Typography>
            <Action tone={TEAL} onClick={() => dispatch({ type: 'SET_ALTERNATE', index })}>
              Use as fallback
            </Action>
          </Box>
        ))
      )}
    </Box>
  );
};

export default NamedRoutes;
